import { useAccess } from '@umijs/max';
import { Alert, Button, Space, Spin, Tag, Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { formatCrmDate } from '@/modules/zy-crm/utils/display';
import { formatFollowUpDate } from '@/modules/zy-crm/utils/follow-up';
import {
  resolveOpportunityOutcome,
  resolveOpportunityOutcomeLabel,
} from '@/modules/zy-crm/utils/opportunity-status';
import type { RelationOption } from '@/modules/zy-crm/utils/relation-options';
import { hasActionAccess } from '@/permissions/helpers';
import { queryFollowUps } from '@/services/bff/follow-up.api';
import type { FollowUpItem } from '@/services/bff/types/follow-up';
import type { OpportunityDrawerProfile } from '@/services/bff/types/opportunity';
import { DetailSectionPanel } from '../detail-section-panel';
import { FOLLOW_UP_BIZ_TYPES } from '../markdown/content';
import { MarkdownView } from '../markdown/view';
import styles from './advance-summary.module.less';

type Props = {
  profile: OpportunityDrawerProfile;
  memberOptions: RelationOption[];
  visibleSectionKeys: readonly string[];
  onAdvance: () => void;
  onCreateFollowUp: () => void;
  onNavigate: (key: 'tasks' | 'followUps') => void;
};

const readableMemberName = (
  uuid: string | undefined,
  directName: string | undefined,
  options: RelationOption[],
) =>
  [directName, options.find((member) => member.value === uuid)?.label]
    .map((name) => name?.trim())
    .find(
      (name) =>
        name &&
        name !== uuid?.trim() &&
        !/^[0-9a-f]{8}-[0-9a-f-]{27}$/i.test(name),
    );

export function OpportunityAdvanceSummary({
  profile,
  memberOptions,
  visibleSectionKeys,
  onAdvance,
  onCreateFollowUp,
  onNavigate,
}: Props) {
  const { opportunity, stageProgress } = profile;
  const access = useAccess() as Record<string, boolean>;
  const canReadFollowUps = visibleSectionKeys.includes('followUps');
  const canReadTasks = visibleSectionKeys.includes('tasks');
  const [attempt, setAttempt] = useState(0);
  const [latest, setLatest] = useState<{
    opportunity: typeof opportunity;
    record?: FollowUpItem;
    canCreate: boolean;
    failed?: boolean;
  }>();
  // The opportunity object changes on detail refresh, but not on navigation count updates.
  useEffect(() => {
    if (!canReadFollowUps) return;
    let active = true;
    setLatest(undefined);
    void queryFollowUps({
      scopes: JSON.stringify([
        { targetType: 'opportunity', targetUuid: opportunity.uuid },
      ]),
      bizType: FOLLOW_UP_BIZ_TYPES.opportunity,
      bizUuid: opportunity.uuid,
      page: 1,
      pageSize: 1,
    })
      .then((result) => {
        if (active)
          setLatest({
            opportunity,
            record: result.list[0],
            canCreate:
              result.permissions?.create ??
              hasActionAccess(access, 'collaboration-follow-ups', 'create'),
          });
      })
      .catch(() => {
        if (active) setLatest({ opportunity, canCreate: false, failed: true });
      });
    return () => {
      active = false;
    };
  }, [
    opportunity,
    canReadFollowUps,
    attempt,
    access['collaboration-follow-ups'],
    access['collaboration-follow-ups:create'],
  ]);

  const stages = stageProgress?.stages || [];
  const stageUuid = stageProgress?.currentStageUuid || opportunity.stageUuid;
  const currentIndex = stages.findIndex((stage) => stage.uuid === stageUuid);
  const outcome = resolveOpportunityOutcome(opportunity, stages[currentIndex]);
  const ongoing = outcome === 'ongoing';
  const hasForwardStage = currentIndex >= 0 && currentIndex < stages.length - 1;
  const rules = stageProgress?.currentStageRules;
  const ruleGroups = [
    { label: '进入标准', values: rules?.entryCriteria },
    { label: '退出标准', values: rules?.exitCriteria },
    { label: '必填项', values: rules?.requiredFields },
    { label: '建议动作', values: rules?.requiredActions },
  ].filter((group) => group.values?.some((value) => value.trim()));
  const enteredAt = stageProgress?.stageEnteredAt;
  const hasEnteredAt = Boolean(enteredAt && dayjs(enteredAt).isValid());
  const task = opportunity.nextActionTask;
  const taskUnavailable = opportunity.nextActionAvailable === false;
  const due = task?.dueTime ? dayjs(task.dueTime) : undefined;
  const overdue =
    ongoing && !taskUnavailable && due?.isValid() && due.isBefore(dayjs());
  const result = latest?.opportunity === opportunity ? latest : undefined;
  const record = result?.record;
  const creatorName = readableMemberName(
    record?.creator,
    undefined,
    memberOptions,
  );
  const ownerName = readableMemberName(
    task?.ownerUserUuid,
    task?.ownerUserName,
    memberOptions,
  );

  return (
    <div className={styles.summary}>
      <DetailSectionPanel
        flat
        title="当前阶段"
        extra={
          ongoing && opportunity.recordPermission?.actions?.edit !== false ? (
            <Tooltip
              title={
                hasForwardStage
                  ? undefined
                  : '当前阶段已是该销售流程的最后一个阶段，请先在「业务设置 → 商机阶段」补充后续阶段'
              }
            >
              <Button
                type="primary"
                disabled={!hasForwardStage}
                onClick={onAdvance}
              >
                推进商机
              </Button>
            </Tooltip>
          ) : null
        }
      >
        <Space wrap size={[12, 8]}>
          <Typography.Text strong>
            {stageProgress?.currentStageName ||
              opportunity.stageName ||
              '尚未配置阶段流程'}
          </Typography.Text>
          {!ongoing && <Tag>{resolveOpportunityOutcomeLabel(outcome)}</Tag>}
          {hasEnteredAt && (
            <Typography.Text type="secondary">
              进入日期：{formatCrmDate(enteredAt, 'date')}
            </Typography.Text>
          )}
          {hasEnteredAt && typeof stageProgress?.stayDays === 'number' && (
            <Typography.Text type="secondary">
              停留 {stageProgress.stayDays} 天
            </Typography.Text>
          )}
        </Space>
        {stages.length > 0 && (
          <ol className={styles.stages} aria-label="销售阶段">
            {stages.map((stage, index) => (
              <li
                key={stage.uuid}
                aria-current={stage.uuid === stageUuid ? 'step' : undefined}
              >
                <span className={styles.stageNumber}>{index + 1}</span>
                {stage.label}
              </li>
            ))}
          </ol>
        )}
        {(ruleGroups.length > 0 ||
          (rules?.maxStayDays !== undefined && rules.maxStayDays > 0)) && (
          <details className={styles.rules}>
            <summary>阶段要求</summary>
            {ruleGroups.map((group) => (
              <p key={group.label}>
                {group.label}：
                {group.values?.filter((value) => value.trim()).join('；')}
              </p>
            ))}
            {rules?.maxStayDays !== undefined && rules.maxStayDays > 0 && (
              <p>建议停留不超过 {rules.maxStayDays} 天</p>
            )}
          </details>
        )}
      </DetailSectionPanel>

      <DetailSectionPanel
        flat
        title="下一步行动"
        extra={
          canReadTasks && (!ongoing || task || taskUnavailable) ? (
            <Button onClick={() => onNavigate('tasks')}>查看相关任务</Button>
          ) : null
        }
      >
        {taskUnavailable ? (
          <Typography.Text type="secondary">
            暂时无法获取下一步行动
          </Typography.Text>
        ) : task ? (
          <Space orientation="vertical" size={4} className={styles.fullWidth}>
            {canReadTasks ? (
              <Button
                type="link"
                className={styles.taskLink}
                onClick={() => onNavigate('tasks')}
              >
                {task.title}
              </Button>
            ) : (
              <Typography.Text strong>{task.title}</Typography.Text>
            )}
            <Space wrap size={[12, 4]}>
              {ownerName && (
                <Typography.Text type="secondary">
                  负责人：{ownerName}
                </Typography.Text>
              )}
              {due?.isValid() && (
                <Typography.Text type={overdue ? 'danger' : 'secondary'}>
                  截止：{formatCrmDate(task.dueTime)}
                </Typography.Text>
              )}
              {overdue && <Tag color="error">已逾期</Tag>}
            </Space>
          </Space>
        ) : (
          <Space wrap>
            <Typography.Text type="secondary">暂无待办行动</Typography.Text>
            {ongoing && canReadTasks && (
              <Button onClick={() => onNavigate('tasks')}>安排下一步</Button>
            )}
          </Space>
        )}
        {opportunity.currentBlockers?.trim() && (
          <div className={styles.blocker}>
            <Typography.Text type="secondary">当前阻塞点：</Typography.Text>
            {opportunity.currentBlockers}
          </div>
        )}
      </DetailSectionPanel>

      {canReadFollowUps && (
        <DetailSectionPanel
          flat
          title="最近跟进"
          extra={
            <Space wrap>
              {result?.canCreate && (
                <Button onClick={onCreateFollowUp}>写跟进</Button>
              )}
              <Button onClick={() => onNavigate('followUps')}>查看全部</Button>
            </Space>
          }
        >
          {!result ? (
            <Spin size="small" aria-label="加载最近跟进" />
          ) : result.failed ? (
            <Alert
              type="error"
              showIcon
              title="最近跟进加载失败"
              action={
                <Button onClick={() => setAttempt((value) => value + 1)}>
                  重试
                </Button>
              }
            />
          ) : record ? (
            <>
              <Space wrap className={styles.followUpMeta}>
                <Typography.Text type="secondary">
                  {formatFollowUpDate(record)}
                </Typography.Text>
                {creatorName && (
                  <Typography.Text type="secondary">
                    {creatorName}
                  </Typography.Text>
                )}
              </Space>
              <div className={styles.preview} inert>
                <MarkdownView
                  content={record.content}
                  contentFormat={record.contentFormat}
                />
              </div>
            </>
          ) : (
            <Typography.Text type="secondary">暂无跟进记录</Typography.Text>
          )}
        </DetailSectionPanel>
      )}
    </div>
  );
}
